import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import BusinessCenterRoundedIcon from "@mui/icons-material/BusinessCenterRounded";
import RepeatRoundedIcon from "@mui/icons-material/RepeatRounded";
import { useNavigate } from "react-router-dom";
import { ownerApi } from "../../../api/owner";
import { formatApiError } from "../../../api/client";
import OwnerClientsSection from "./commercial/OwnerClientsSection";
import OwnerServicesSection from "./commercial/OwnerServicesSection";
import OwnerSalesSection from "./commercial/OwnerSalesSection";
import OwnerRecurringFinanceDialog from "./center/OwnerRecurringFinanceDialog";
import { money } from "./commercial/OwnerCommercialUi";

function CurrencyValues({ values, empty = "—" }) {
  const entries = Object.entries(values || {});
  if (!entries.length) return <Typography variant="h6" sx={{ fontWeight: 950 }}>{empty}</Typography>;
  return entries.map(([currency, value]) => (
    <Typography key={currency} variant="h6" sx={{ fontWeight: 950 }}>{money(value, currency)}</Typography>
  ));
}

function RecurringValues({ values, field, empty = "—" }) {
  const entries = Object.entries(values || {}).filter(([, row]) => Number(row?.[field] || 0) !== 0 || Number(row?.activeAgreements || 0) > 0);
  if (!entries.length) return <Typography variant="h6" sx={{ fontWeight: 950 }}>{empty}</Typography>;
  return entries.map(([currency, row]) => (
    <Box key={`${field}:${currency}`}>
      <Typography variant="h6" sx={{ fontWeight: 950 }}>{money(row?.[field] || 0, currency)}</Typography>
      <Typography sx={{ mt: .1, color: "text.secondary", fontSize: ".68rem", fontWeight: 750 }}>
        {Number(row?.activeAgreements || 0)} acuerdo{Number(row?.activeAgreements || 0) === 1 ? "" : "s"}{Number(row?.indefiniteAgreements || 0) ? ` · ${row.indefiniteAgreements} sin fecha de fin` : ""}
      </Typography>
    </Box>
  ));
}

export default function OwnerCommercialPage() {
  const navigate = useNavigate();
  const [tab, setTab] = React.useState(0);
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [recurringOpen, setRecurringOpen] = React.useState(false);

  const load = React.useCallback(async () => {
    setError("");
    try {
      const [summaryResponse, clientsResponse, servicesResponse, salesResponse, accountsResponse, initiativesResponse] = await Promise.all([
        ownerApi.commercialSummary(),
        ownerApi.listClients(),
        ownerApi.listServices({ includeInactive: true }),
        ownerApi.listSales(),
        ownerApi.listFinanceAccounts(),
        ownerApi.listInitiatives({ status: "", limit: 100 }),
      ]);
      setData({
        summary: summaryResponse.summary || {},
        clients: clientsResponse.clients || [],
        services: servicesResponse.services || [],
        sales: salesResponse.sales || [],
        accounts: accountsResponse.accounts || [],
        initiatives: initiativesResponse.initiatives || [],
      });
    } catch (err) {
      setError(formatApiError(err, "No se pudo cargar tu operación personal"));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  return (
    <Box sx={{ pb: 5 }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ md: "center" }} sx={{ mb: 2.2 }}>
        <Box>
          <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate("/dashboard/owner")} sx={{ mb: .5, textTransform: "none", fontWeight: 850 }}>
            Owner Panel
          </Button>
          <Stack direction="row" spacing={1} alignItems="center">
            <BusinessCenterRoundedIcon sx={{ color: "#F35A3A" }} />
            <Typography variant="h4" sx={{ fontWeight: 1000, letterSpacing: "-.035em" }}>Mi operación</Typography>
          </Stack>
          <Typography sx={{ mt: .6, color: "text.secondary", maxWidth: 820 }}>
            Clientes, servicios, trabajos, ventas y cobros personales. Comercial registra lo que vendés; Finanzas registra cuándo el dinero realmente entra.
          </Typography>
        </Box>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Button variant="contained" startIcon={<RepeatRoundedIcon />} onClick={() => setRecurringOpen(true)} sx={{ textTransform: "none", fontWeight: 900 }}>
            Nuevo recurrente
          </Button>
          <Button variant="outlined" onClick={() => navigate("/dashboard/owner/finanzas")} sx={{ textTransform: "none", fontWeight: 900 }}>
            Ir a Mis Finanzas
          </Button>
        </Stack>
      </Stack>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      {loading ? <Box sx={{ py: 6, textAlign: "center" }}><CircularProgress size={30} /></Box> : null}

      {data ? (
        <>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2,minmax(0,1fr))", xl: "repeat(4,minmax(0,1fr))" }, gap: 1.2, mb: 2.2 }}>
            <Card variant="outlined" sx={{ borderRadius: 3 }}><CardContent><Typography variant="overline" sx={{ fontWeight: 900 }}>CLIENTES</Typography><Typography variant="h5" sx={{ fontWeight: 1000 }}>{data.summary.activeClients || 0}</Typography></CardContent></Card>
            <Card variant="outlined" sx={{ borderRadius: 3 }}><CardContent><Typography variant="overline" sx={{ fontWeight: 900 }}>SERVICIOS</Typography><Typography variant="h5" sx={{ fontWeight: 1000 }}>{data.summary.activeServices || 0}</Typography></CardContent></Card>
            <Card variant="outlined" sx={{ borderRadius: 3 }}><CardContent><Typography variant="overline" sx={{ fontWeight: 900 }}>COBRADO</Typography><CurrencyValues values={data.summary.collectedByCurrency} /></CardContent></Card>
            <Card variant="outlined" sx={{ borderRadius: 3 }}><CardContent><Typography variant="overline" sx={{ fontWeight: 900 }}>POR COBRAR · CONTRATOS</Typography><CurrencyValues values={data.summary.outstandingByCurrency} /></CardContent></Card>
            <Card variant="outlined" sx={{ borderRadius: 3 }}><CardContent><Typography variant="overline" sx={{ fontWeight: 900 }}>RECURRENTE / MES</Typography><RecurringValues values={data.summary.recurringByCurrency} field="revenueMonthly" /><Typography sx={{ mt: .4, color: "text.secondary", fontSize: ".68rem" }}>Ingreso contratado equivalente mensual. No se suma como dinero cobrado.</Typography></CardContent></Card>
            <Card variant="outlined" sx={{ borderRadius: 3 }}><CardContent><Typography variant="overline" sx={{ fontWeight: 900 }}>COSTO RECURRENTE / MES</Typography><RecurringValues values={data.summary.recurringByCurrency} field="costMonthly" /><Typography sx={{ mt: .4, color: "text.secondary", fontSize: ".68rem" }}>Tu costo estimado para sostener esos servicios.</Typography></CardContent></Card>
            <Card variant="outlined" sx={{ borderRadius: 3 }}><CardContent><Typography variant="overline" sx={{ fontWeight: 900 }}>MARGEN RECURRENTE / MES</Typography><RecurringValues values={data.summary.recurringByCurrency} field="marginMonthly" /><Typography sx={{ mt: .4, color: "text.secondary", fontSize: ".68rem" }}>Precio acordado menos tu costo recurrente.</Typography></CardContent></Card>
            <Card variant="outlined" sx={{ borderRadius: 3 }}><CardContent><Typography variant="overline" sx={{ fontWeight: 900 }}>MARGEN · 12 MESES</Typography><RecurringValues values={data.summary.recurringByCurrency} field="marginAnnualized" /><Typography sx={{ mt: .4, color: "text.secondary", fontSize: ".68rem" }}>Proyección anualizada si los acuerdos activos continúan.</Typography></CardContent></Card>
          </Box>

          <Card variant="outlined" sx={{ borderRadius: 3, mb: 2 }}>
            <Tabs value={tab} onChange={(_, next) => setTab(next)} variant="scrollable" scrollButtons="auto">
              <Tab label={`Ventas (${data.sales.length})`} />
              <Tab label={`Clientes (${data.clients.length})`} />
              <Tab label={`Servicios (${data.services.filter((service) => service.active).length})`} />
            </Tabs>
          </Card>

          {tab === 0 ? <OwnerSalesSection sales={data.sales} clients={data.clients} services={data.services} accounts={data.accounts} initiatives={data.initiatives} onRefresh={load} /> : null}
          {tab === 1 ? <OwnerClientsSection clients={data.clients} onRefresh={load} /> : null}
          {tab === 2 ? <OwnerServicesSection services={data.services} onRefresh={load} /> : null}
        </>
      ) : null}

      <OwnerRecurringFinanceDialog
        open={recurringOpen}
        onClose={() => setRecurringOpen(false)}
        accounts={data?.accounts || []}
        onSaved={async () => { setRecurringOpen(false); await load(); }}
      />
    </Box>
  );
}
